// eX1 zone clustering isn't ported on the backend yet, so we synthesize
// zones from seat data when topology.zones is empty.
import type { TopologyPayload } from "./types/topology";


export type ResolvedZone = TopologyPayload["zones"][number] & {
  synthesized: boolean;
};


export function getAllZones(topology: TopologyPayload): ResolvedZone[] {
  if (topology.zones.length > 0) {
    return topology.zones.map((z) => ({ ...z, synthesized: false }));
  }
  const isEx3 = topology.layout.topology.type === "nc-port";
  const combos = new Map<string, { nc: number; port: number }>();
  for (const s of Object.values(topology.seats)) {
    const ctrl = isEx3 ? s.nc : s.mcu;
    if (ctrl === undefined || ctrl === null) continue;
    const key = `${ctrl}-${s.port}`;
    if (!combos.has(key)) combos.set(key, { nc: ctrl, port: s.port });
  }
  const sorted = [...combos.values()].sort(
    (a, b) => a.nc - b.nc || a.port - b.port,
  );
  return sorted
    .map((c) => resolveZone(topology, c.nc, c.port)?.zone)
    .filter((z): z is ResolvedZone => z !== undefined);
}


export function resolveZone(
  topology: TopologyPayload,
  nc: number,
  port: number,
): { zone: ResolvedZone; index: number } | null {
  const existingIdx = topology.zones.findIndex(
    (z) => z.nc === nc && z.port === port,
  );
  if (existingIdx >= 0) {
    return {
      zone: { ...topology.zones[existingIdx], synthesized: false },
      index: existingIdx,
    };
  }

  const isEx3 = topology.layout.topology.type === "nc-port";
  const seatsHere = Object.values(topology.seats).filter(
    (s) => (isEx3 ? s.nc : s.mcu) === nc && s.port === port,
  );
  if (seatsHere.length === 0) return null;

  const colour =
    topology.layout.topology.zone_colors[`${nc}-${port}`] ?? "#888888";
  const cabin = seatsHere[0]?.cabin ?? "";
  const hpsValues = seatsHere
    .map((s) => s.hps)
    .filter((h): h is number => h !== null && h !== undefined);
  const hpsSet = new Set(hpsValues);

  return {
    zone: {
      nc, port,
      color: colour,
      cabin,
      sib_start: "",
      sib_start_seb: 0,
      sib_end: "",
      sib_end_seb: 0,
      seat_count: seatsHere.length,
      hps_count: hpsSet.size,
      hps_list: [...hpsSet].sort((a, b) => a - b),
      redundant_nc: 0,
      redundant_port: 0,
      synthesized: true,
    },
    index: -1,
  };
}
